import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import DateStamp from "@components/dates/DateStamp";
import styles from "@components/expert/ExpertDashboardMetricPage.module.css";
import ExpertKPIsFilters from "@components/forms/ExpertKPIsFilters";
import HandleQuery from "@components/HandleQuery";
import Nbsp from "@components/Nbsp";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import Table, { TableHeader } from "@components/Table";
import IconBase from "@components/text/IconBase";
import PageTitle from "@components/text/PageTitle";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { TimePeriodEnum } from "@typing/Enums";
import {
  ClientAccessTypeEnum,
  ExpertClientsSortByEnum,
  ExpertGlobalRoleEnum,
  GetExpertClientsDocument,
  GetExpertClientsQuery,
  GetExpertClientsQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";
import { countryNameForCode } from "@utils/countryUtils";
import { calculateDatesFromTimePeriod, getCareProfileName } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { timezoneAbbreviation } from "@utils/timeUtils";

type Filters = ComponentProps<typeof ExpertKPIsFilters>["filters"];

const ExpertDashboardSignUpsPage = () => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const [sortBy, setSortBy] = useState<ExpertClientsSortByEnum>(ExpertClientsSortByEnum.CREATED_AT);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);

  const [filters, setFilters] = useState<Filters>({
    endDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).endDate,
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
    startDate: calculateDatesFromTimePeriod(TimePeriodEnum.SEVEN_DAYS, null, null, intl).startDate,
    timePeriod: TimePeriodEnum.SEVEN_DAYS
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getExpertClients, getExpertClientsQuery] = useLazyQuery<GetExpertClientsQuery, GetExpertClientsQueryVariables>(
    GetExpertClientsDocument
  );

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    if (myExpert) {
      getExpertClients({
        variables: {
          endDate: dates.endDate,
          expertId: filters.expertId,
          primaryJourneys: true,
          sortBy,
          sortDirection,
          startDate: dates.startDate
        }
      });
    }
  }, [filters, getExpertClients, intl, myExpert, sortBy, sortDirection]);

  const clients = useNodesFromConnection(getExpertClientsQuery.data?.expertClients);

  const tableHeaders: TableHeader<ExpertClientsSortByEnum>[] = [
    { columnSortBy: ExpertClientsSortByEnum.CREATED_AT },
    { columnSortBy: ExpertClientsSortByEnum.NAME },
    { messageKey: "pages.expert.clients.tableHeadings.lovedOnes" },
    { messageKey: "pages.expert.clients.tableHeadings.employer" },
    { messageKey: "pages.expert.clients.tableHeadings.country" },
    { messageKey: "pages.expert.clients.tableHeadings.timezone" }
  ];

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.dashboardSignUps.list">
      <PageTitle imageName="peopleCoffeeLight" titleKey="pages.expert.dashboard.signUps.title" />
      <div>
        <Button fill="clear" routerLink={`/expert/dashboard${location.search}`}>
          <IconBase icon="chevronLeft" slot="start" />
          <FormattedMessage id="pages.expert.dashboard.consults.dashboard" />
        </Button>
      </div>

      <div className={styles.flex}>
        <div
          className={`${styles.filters} ${filters.timePeriod === TimePeriodEnum.CUSTOM ? styles.biggerFilters : ""}`}
        >
          <ExpertKPIsFilters filters={filters} onChange={handleFilterChange} />
        </div>
        {getExpertClientsQuery.data && (
          <div className={styles.infoBox}>
            <div className={styles.infoBoxLabel}>
              <FormattedMessage id="pages.expert.dashboard.signUps.total" />
            </div>
            <div className={styles.infoBoxData}>{getExpertClientsQuery.data?.expertClients.totalCount}</div>
          </div>
        )}
      </div>

      <HandleQuery query={getExpertClientsQuery}>
        {data => (
          <Table<ExpertClientsSortByEnum>
            fetchMore={getExpertClientsQuery.fetchMore}
            headerKey="pages.expert.clients.tableHeadings"
            headers={tableHeaders}
            pageInfo={data?.expertClients.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {clients.map(client => (
              <tr key={client.id}>
                <td>
                  <DateStamp showWeekday={false} value={client.createdAt} />
                </td>
                <td>
                  {client.lastName}
                  <FormattedMessage id="dictionary.punctuation.comma" />
                  <Nbsp />
                  {client.firstName}
                </td>
                <td>
                  {client.roles.map(role => (
                    <div key={role.id}>
                      <InternalLink
                        linkEventCreateVariables={{ journeyId: role.journey.id }}
                        to={journeyRoute({ journeyId: role.journey.id, linkAfterJourney: "", myExpert })}
                      >
                        {getCareProfileName({ intl, journey: role.journey })}
                      </InternalLink>
                    </div>
                  ))}
                </td>
                <td>
                  {client.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                    client.employer?.name
                  ) : (
                    <FormattedMessage id="dictionary.selfPaid" />
                  )}
                </td>
                <td>{countryNameForCode(client.location.countryCode)}</td>
                <td>{client.timezone && <>{timezoneAbbreviation(client.timezone)}</>}</td>
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertDashboardSignUpsPage;
